/**
 * Purpose: Overlay drawer showing the current player's full 9-period class schedule.
 * Owns: Schedule list with period number, subject, emoji, and completion status
 *       (completed with letter grade, current, or upcoming).
 * Notes: Opened from a button on GameplayPage. Dismissed by tapping outside or close button.
 *        Uses existing chalk styling on a dark overlay background (CS-22).
 */

import type { Player, PlayerClassProgress, ClassPeriod } from '../../types/game';
import type { Category } from '../../types/question';
import { FINAL_EXAM_PERIOD } from '../../config/classConfig';

// _______________________________________________
//              SUBJECT EMOJI ICONS
// _______________________________________________
// Matches the emoji map used elsewhere in the app (CS-20: no magic strings).

const SUBJECT_EMOJIS: Record<Category | 'Final Exam', string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
  'Final Exam': '\u{1F393}',
};

// _______________________________________________
//              STATUS INDICATORS
// _______________________________________________

const STATUS_COMPLETED = '\u2705';   // checkmark
const STATUS_CURRENT = '\uD83D\uDCCD';  // pin / map marker
const STATUS_UPCOMING = '\u2B1C';    // white square

interface ScheduleDrawerProps {
  /** Whether the drawer is currently visible */
  isOpen: boolean;
  /** Called when the drawer should close */
  onClose: () => void;
  /** The player whose schedule to display */
  player: Player;
}

export function ScheduleDrawer({ isOpen, onClose, player }: ScheduleDrawerProps) {
  if (!isOpen) return null;

  return (
    /* Dark overlay — tap anywhere on the backdrop to dismiss */
    <div
      style={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${player.name}'s schedule`}
    >
      {/* Drawer panel — stop click propagation so tapping inside doesn't dismiss */}
      <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
        {/* Header with player name and close button */}
        <div style={styles.header}>
          <h2 className="chalk-text-bright" style={styles.title}>
            {player.name}&rsquo;s Schedule
          </h2>
          <button
            style={styles.closeButton}
            onClick={onClose}
            aria-label="Close schedule"
          >
            &times;
          </button>
        </div>

        {/* Schedule rows */}
        <div style={styles.scheduleList}>
          {player.schedule.map((period, index) => (
            <ScheduleRow
              key={period.periodNumber}
              period={period}
              progress={player.classProgress[index]}
              isCurrent={period.periodNumber === player.currentPeriod}
              isCompleted={player.classProgress[index]?.passed ?? false}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// _______________________________________________
//              SCHEDULE ROW
// _______________________________________________

function ScheduleRow({
  period,
  progress,
  isCurrent,
  isCompleted,
}: {
  period: ClassPeriod;
  progress: PlayerClassProgress | undefined;
  isCurrent: boolean;
  isCompleted: boolean;
}) {
  const emoji = SUBJECT_EMOJIS[period.subject as keyof typeof SUBJECT_EMOJIS] ?? '';
  const isFinalExam = period.periodNumber === FINAL_EXAM_PERIOD;

  /* Determine status indicator and label */
  let statusIcon: string;
  let statusLabel: string;

  if (isCompleted) {
    const grade = progress?.letterGrade ?? '?';
    statusIcon = STATUS_COMPLETED;
    statusLabel = grade;
  } else if (isCurrent) {
    statusIcon = STATUS_CURRENT;
    statusLabel = 'Now';
  } else {
    statusIcon = STATUS_UPCOMING;
    statusLabel = '';
  }

  /* Highlight styling for the current period row */
  const rowStyle: React.CSSProperties = {
    ...styles.row,
    ...(isCurrent ? styles.currentRow : {}),
    ...(isCompleted ? styles.completedRow : {}),
  };

  return (
    <div style={rowStyle}>
      {/* Period number */}
      <span className="chalk-text" style={styles.periodNumber}>
        {isFinalExam ? 'FE' : period.periodNumber}
      </span>

      {/* Subject emoji */}
      <span style={styles.subjectEmoji}>{emoji}</span>

      {/* Subject name */}
      <span
        className="chalk-text"
        style={{
          ...styles.subjectName,
          ...(isCompleted ? styles.completedText : {}),
        }}
      >
        {period.subject}
      </span>

      {/* Status: icon + grade for completed */}
      <span style={styles.statusArea}>
        <span style={styles.statusIcon}>{statusIcon}</span>
        {statusLabel && (
          <span
            className="chalk-text"
            style={{
              ...styles.statusLabel,
              ...(isCompleted ? styles.gradeLabel : styles.currentLabel),
            }}
          >
            {statusLabel}
          </span>
        )}
      </span>
    </div>
  );
}

// _______________________________________________
//              STYLES
// _______________________________________________
// All colors reference theme tokens (CS-22).

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '1rem',
  },
  drawer: {
    backgroundColor: 'var(--bg-chalkboard)',
    border: '3px solid var(--accent-wood)',
    borderRadius: '12px',
    padding: '1rem',
    width: '100%',
    maxWidth: '380px',
    maxHeight: '80vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid var(--accent-wood)',
    paddingBottom: '0.5rem',
  },
  title: {
    fontSize: '1.2rem',
    fontFamily: 'var(--font-family-display)',
    margin: 0,
  },
  closeButton: {
    fontSize: '1.5rem',
    color: 'var(--text-light)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
    opacity: 0.7,
    minHeight: '48px',
    minWidth: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scheduleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },

  /* Individual row */
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.5rem',
    borderRadius: '8px',
    backgroundColor: 'transparent',
  },
  currentRow: {
    backgroundColor: 'rgba(212, 168, 67, 0.15)',
    border: '1px solid var(--accent-gold)',
  },
  completedRow: {
    opacity: 0.65,
  },
  periodNumber: {
    fontSize: '0.85rem',
    fontWeight: 700,
    width: '24px',
    textAlign: 'center' as const,
    flexShrink: 0,
    opacity: 0.7,
  },
  subjectEmoji: {
    fontSize: '1.2rem',
    width: '28px',
    textAlign: 'center' as const,
    flexShrink: 0,
  },
  subjectName: {
    flex: 1,
    fontSize: '1rem',
    fontFamily: 'var(--font-family-handwriting)',
  },
  completedText: {
    textDecoration: 'line-through',
    opacity: 0.8,
  },
  statusArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    minWidth: '55px',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  statusIcon: {
    fontSize: '1rem',
  },
  statusLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  gradeLabel: {
    color: 'var(--accent-gold)',
  },
  currentLabel: {
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family-handwriting)',
  },
};
